import configService from "@/configs/config";
import livekitService from "@/services/Livekit";
import { Room, RoomEvent, Track } from "livekit-client";

const LIVEKIT_URL = configService.livekitUrl;

let activeRoom = null;
let localDisplayName = "";
let currentSpeakers = new Set();
const participantSessionId = `${Date.now().toString(36)}-${Math.random()
  .toString(36)
  .slice(2, 10)}`;

function sanitizeParticipantValue(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function buildParticipantIdentity(participantIdentity, participantName) {
  const explicitIdentity = sanitizeParticipantValue(participantIdentity);
  const fallbackIdentity =
    sanitizeParticipantValue(participantName) || "participant";
  const baseIdentity = explicitIdentity
    ? `user-${explicitIdentity}`
    : `guest-${fallbackIdentity}`;

  return `${baseIdentity}-${participantSessionId}`;
}

function buildParticipantMetadata({
  participantName,
  participantIdentity,
  isModerator,
}) {
  return JSON.stringify({
    displayName: participantName,
    participantIdentity: participantIdentity
      ? String(participantIdentity)
      : null,
    isModerator,
    sessionId: participantSessionId,
  });
}

function getRoom() {
  return activeRoom;
}

async function connectToRoom(
  roomName,
  participantName,
  isModerator = false,
  participantIdentity = "",
  options = {}
) {
  if (activeRoom) {
    await disconnectFromRoom();
  }

  const normalizedParticipantIdentity = buildParticipantIdentity(
    participantIdentity,
    participantName
  );

  const metadata = buildParticipantMetadata({
    participantName,
    participantIdentity,
    isModerator,
  });

  // tokenEndpoint "breakout": token de sub-sala emitido por el endpoint de
  // networking (gestor-api valida moderador y patrón de sala en el servidor)
  const tokenResponse =
    options.tokenEndpoint === "breakout"
      ? await livekitService.generateBreakoutToken({
          mainRoom: options.mainRoom,
          targetRoom: roomName,
          participantName,
          participantIdentity: normalizedParticipantIdentity,
          metadata,
        })
      : await livekitService.generateToken({
          roomName,
          participantName,
          participantIdentity: normalizedParticipantIdentity,
          metadata,
          isModerator,
        });

  const serverUrl = tokenResponse.url || LIVEKIT_URL;

  const room = new Room({
    adaptiveStream: true,
    dynacast: true,
  });

  setupRoomEventListeners(room);

  try {
    activeRoom = room;
    localDisplayName = participantName || "";
    currentSpeakers = new Set();

    await activeRoom.connect(serverUrl, tokenResponse.token);

    // Las pistas que ya estaban publicadas se suscriben mientras el
    // componente aún muestra "conectando" (sin #participants-grid ni
    // #screenshare-container). El componente llama a attachExistingTracks()
    // cuando el DOM de la sala ya existe.

    // Todos ingresan con cámara y micrófono apagados; se activan
    // manualmente (o cuando el moderador concede permiso).
    return activeRoom;
  } catch (error) {
    console.error("[LiveKit] Error conectando a la sala", {
      roomName,
      participantName,
      participantIdentity: normalizedParticipantIdentity,
      isModerator,
      serverUrl,
      message: error?.message,
      name: error?.name,
    });

    try {
      await room.disconnect();
    } catch (disconnectError) {
      console.warn(
        "[LiveKit] No se pudo limpiar la conexión fallida",
        disconnectError
      );
    }

    if (activeRoom === room) {
      activeRoom = null;
    }

    throw error;
  }
}

function setupRoomEventListeners(roomInstance) {
  roomInstance.on(
    RoomEvent.TrackSubscribed,
    (track, publication, participant) => {
      handleTrackSubscribed(track, publication, participant);
    }
  );

  roomInstance.on(
    RoomEvent.TrackUnsubscribed,
    (track, publication, participant) => {
      handleTrackUnsubscribed(track, publication, participant);
    }
  );

  roomInstance.on(RoomEvent.ParticipantConnected, (participant) => {
    handleParticipantConnected(participant);
  });

  roomInstance.on(RoomEvent.ParticipantDisconnected, (participant) => {
    handleParticipantDisconnected(participant);
  });

  // Indicador de quién está hablando (estilo Zoom)
  roomInstance.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
    currentSpeakers = new Set(speakers.map((speaker) => speaker.identity));
    syncTiles();
  });

  // Estado del micrófono en los tiles
  roomInstance.on(RoomEvent.TrackMuted, () => syncTiles());
  roomInstance.on(RoomEvent.TrackUnmuted, () => syncTiles());
  roomInstance.on(RoomEvent.LocalTrackPublished, () => syncTiles());
  roomInstance.on(RoomEvent.LocalTrackUnpublished, () => syncTiles());

  roomInstance.on(RoomEvent.Disconnected, () => {
    handleDisconnected();
  });

  roomInstance.on(RoomEvent.Reconnecting, () => {
    console.warn("[LiveKit] Reconectando...");
  });

  roomInstance.on(RoomEvent.Reconnected, () => {
    console.info("[LiveKit] Reconexión exitosa");
  });

  roomInstance.on(
    RoomEvent.ConnectionQualityChanged,
    (quality, participant) => {
      console.debug(
        `[LiveKit] Calidad de conexión de ${participant.identity}: ${quality}`
      );
    }
  );

  // Señales del servidor (breakouts, moderación) y de otros participantes
  roomInstance.on(RoomEvent.DataReceived, (payload, participant) => {
    try {
      const message = JSON.parse(new TextDecoder().decode(payload));
      window.dispatchEvent(
        new CustomEvent("livekit-data-received", {
          detail: { ...message, senderIdentity: participant?.identity || null },
        })
      );
    } catch (error) {
      console.warn("[LiveKit] Data message no parseable", error);
    }
  });

  // Cambios de metadata (p.ej. mano levantada) y permisos (grant-publish)
  roomInstance.on(RoomEvent.ParticipantMetadataChanged, () => {
    window.dispatchEvent(new CustomEvent("livekit-participants-changed"));
  });

  roomInstance.on(RoomEvent.ParticipantPermissionsChanged, (prev, participant) => {
    window.dispatchEvent(
      new CustomEvent("livekit-permissions-changed", {
        detail: { identity: participant?.identity || null },
      })
    );
  });
}

function handleTrackSubscribed(track, publication, participant) {
  // Audio ya reproduciéndose: no duplicar el <audio> (attachExistingTracks
  // puede volver a pasar por una pista que TrackSubscribed ya adjuntó)
  if (
    track.kind === Track.Kind.Audio &&
    track.attachedElements.some((el) => el.isConnected)
  ) {
    return;
  }

  if (
    track.source === Track.Source.ScreenShare ||
    track.source === Track.Source.ScreenShareAudio
  ) {
    if (track.kind === Track.Kind.Video) {
      const element = track.attach();
      element.id = `screenshare-${participant.identity}`;
      element.style.width = "100%";
      element.style.height = "100%";
      element.style.objectFit = "contain";

      const container = document.getElementById("screenshare-container");
      if (container) {
        container.innerHTML = "";
        container.appendChild(element);

        // Disparar evento personalizado para que Vue lo sepa
        window.dispatchEvent(
          new CustomEvent("livekit-screenshare-started", {
            detail: { participantId: participant.identity },
          })
        );
      }
    } else if (track.kind === Track.Kind.Audio) {
      const element = track.attach();
      element.id = `screenshare-audio-${participant.identity}`;
      element.style.display = "none";
      document.body.appendChild(element);
    }
  } else if (track.kind === Track.Kind.Video) {
    const element = track.attach();
    element.id = `video-${participant.identity}`;
    // El video debe llenar el tile del participante por completo
    element.style.width = "100%";
    element.style.height = "100%";
    element.style.objectFit = "cover";

    const container = document.getElementById("participants-grid");
    if (container) {
      let wrapper = document.getElementById(
        `participant-${participant.identity}`
      );
      if (!wrapper) {
        wrapper = createParticipantWrapper(participant);
        container.appendChild(wrapper);
      }
      const existingVideo = wrapper.querySelector("video");
      if (existingVideo) {
        wrapper.removeChild(existingVideo);
      }
      wrapper.appendChild(element);

      const nameLabel = wrapper.querySelector(".livekit-participant-name");
      if (!nameLabel) {
        const newNameLabel = document.createElement("div");
        newNameLabel.className = "livekit-participant-name";
        newNameLabel.textContent = participant.name || participant.identity;
        wrapper.appendChild(newNameLabel);
      }
    }
  } else if (track.kind === Track.Kind.Audio) {
    const element = track.attach();
    element.id = `audio-${participant.identity}`;
    element.style.display = "none";
    document.body.appendChild(element);
  }
}

/**
 * Adjunta las pistas remotas ya suscritas. Quien entra tarde recibe
 * TrackSubscribed antes de que exista el DOM de la sala y esas pistas se
 * perdían: cámaras y pantalla compartida publicadas antes de su ingreso no
 * se veían. Es idempotente.
 */
function attachExistingTracks() {
  if (!activeRoom) return;
  for (const participant of activeRoom.remoteParticipants.values()) {
    for (const publication of participant.trackPublications.values()) {
      if (publication.track && publication.isSubscribed) {
        handleTrackSubscribed(publication.track, publication, participant);
      }
    }
  }
}

function handleTrackUnsubscribed(track, publication, participant) {
  if (
    track.source === Track.Source.ScreenShare ||
    track.source === Track.Source.ScreenShareAudio
  ) {
    track.detach().forEach((el) => el.remove());

    // Disparar evento personalizado
    window.dispatchEvent(
      new CustomEvent("livekit-screenshare-stopped", {
        detail: { participantId: participant.identity },
      })
    );

    const container = document.getElementById("screenshare-container");
    if (container) {
      container.innerHTML = "";
    }
  } else if (track.kind === Track.Kind.Video) {
    track.detach().forEach((el) => el.remove());
    const wrapper = document.getElementById(
      `participant-${participant.identity}`
    );
    if (wrapper) {
      const existingVideo = wrapper.querySelector("video");
      if (existingVideo) {
        wrapper.removeChild(existingVideo);
      }
    }
  } else if (track.kind === Track.Kind.Audio) {
    track.detach().forEach((el) => el.remove());
  }
}

function handleParticipantConnected(participant) {
  console.info(`[LiveKit] Participante conectado: ${participant.identity}`);
  syncTiles();
}

/**
 * ¿El participante es el moderador de la sala? Se lee de la metadata que
 * publica el cliente al conectarse y del `role` que firma el token en
 * gestor-api.
 */
function isModeratorParticipant(participant) {
  try {
    const metadata = JSON.parse(participant.metadata || "{}");
    return metadata.isModerator === true || metadata.role === "moderator";
  } catch (e) {
    return false;
  }
}

function displayNameFor(participant) {
  try {
    const metadata = JSON.parse(participant.metadata || "{}");
    if (metadata.displayName) return metadata.displayName;
  } catch (e) {
    // metadata no parseable: usar name/identity
  }
  return participant.name || participant.identity;
}

function createParticipantWrapper(participant, label) {
  const wrapper = document.createElement("div");
  wrapper.id = `participant-${participant.identity}`;
  wrapper.className = "livekit-participant";

  const placeholder = document.createElement("div");
  placeholder.className = "livekit-room__video-placeholder";

  const avatar = document.createElement("div");
  avatar.className = "livekit-room__placeholder-avatar";
  const icon = document.createElement("i");
  icon.className = "fas fa-user";
  avatar.appendChild(icon);
  placeholder.appendChild(avatar);

  const namePlate = document.createElement("span");
  namePlate.className = "livekit-room__placeholder-name";
  namePlate.textContent = label || displayNameFor(participant);
  placeholder.appendChild(namePlate);

  wrapper.appendChild(placeholder);

  const nameTag = document.createElement("div");
  nameTag.className = "livekit-participant-name";
  nameTag.textContent = label || displayNameFor(participant);
  wrapper.appendChild(nameTag);

  // Indicador de moderador (se muestra según la clase del wrapper)
  const modBadge = document.createElement("div");
  modBadge.className = "livekit-participant-mod";
  const modIcon = document.createElement("i");
  modIcon.className = "fas fa-crown";
  modBadge.appendChild(modIcon);
  modBadge.appendChild(document.createTextNode(" Moderador"));
  wrapper.appendChild(modBadge);

  // Indicador de micrófono silenciado
  const micBadge = document.createElement("div");
  micBadge.className = "livekit-participant-mic";
  const micIcon = document.createElement("i");
  micIcon.className = "fas fa-microphone-slash";
  micBadge.appendChild(micIcon);
  wrapper.appendChild(micBadge);

  return wrapper;
}

function isMicrophoneLive(participant) {
  for (const [, publication] of participant.audioTrackPublications) {
    if (
      publication.source === "microphone" &&
      publication.track &&
      !publication.isMuted
    ) {
      return true;
    }
  }
  return false;
}

function updateTileState(wrapper, participant) {
  wrapper.classList.toggle(
    "livekit-participant--mic-off",
    !isMicrophoneLive(participant)
  );
  wrapper.classList.toggle(
    "livekit-participant--speaking",
    currentSpeakers.has(participant.identity)
  );
  wrapper.classList.toggle(
    "livekit-participant--moderator",
    isModeratorParticipant(participant)
  );
}

/**
 * Sincroniza la cuadrícula estilo Zoom: un tile por participante
 * (incluido el local y los que no tienen cámara), elimina los que se
 * fueron y actualiza indicadores de voz y micrófono. Es idempotente.
 */
function syncTiles() {
  if (!activeRoom) return;
  const container = document.getElementById("participants-grid");
  if (!container) return;

  const expected = new Set();

  const local = activeRoom.localParticipant;
  if (local && local.identity) {
    expected.add(local.identity);
    let wrapper = document.getElementById(`participant-${local.identity}`);
    if (!wrapper) {
      wrapper = createParticipantWrapper(
        local,
        localDisplayName ? `Tú (${localDisplayName})` : "Tú"
      );
      wrapper.classList.add("livekit-participant--local");
      container.prepend(wrapper);
    }
    updateTileState(wrapper, local);
  }

  for (const participant of activeRoom.remoteParticipants.values()) {
    expected.add(participant.identity);
    let wrapper = document.getElementById(
      `participant-${participant.identity}`
    );
    if (!wrapper) {
      wrapper = createParticipantWrapper(participant);
      container.appendChild(wrapper);
    }
    updateTileState(wrapper, participant);
  }

  for (const child of Array.from(container.children)) {
    const identity = (child.id || "").replace(/^participant-/, "");
    if (identity && !expected.has(identity)) {
      child.remove();
    }
  }
}

function handleParticipantDisconnected(participant) {
  console.info(`[LiveKit] Participante desconectado: ${participant.identity}`);
  const wrapper = document.getElementById(
    `participant-${participant.identity}`
  );
  if (wrapper) {
    wrapper.remove();
  }
  syncTiles();
}

function handleDisconnected() {
  console.info("[LiveKit] Desconectado de la sala");
  activeRoom = null;
  currentSpeakers = new Set();
  const container = document.getElementById("participants-grid");
  if (container) {
    container.innerHTML = "";
  }
  window.dispatchEvent(new CustomEvent("livekit-room-disconnected"));
}

async function disconnectFromRoom() {
  if (activeRoom) {
    await activeRoom.disconnect();
    activeRoom = null;
  }
}

async function toggleAudio() {
  if (!activeRoom) return false;
  const enabled = activeRoom.localParticipant.isMicrophoneEnabled;
  await activeRoom.localParticipant.setMicrophoneEnabled(!enabled);
  return !enabled;
}

async function toggleVideo() {
  if (!activeRoom) return false;
  const enabled = activeRoom.localParticipant.isCameraEnabled;
  await activeRoom.localParticipant.setCameraEnabled(!enabled);
  return !enabled;
}

async function toggleScreenShare() {
  if (!activeRoom) return false;
  const enabled = activeRoom.localParticipant.isScreenShareEnabled;
  await activeRoom.localParticipant.setScreenShareEnabled(!enabled);

  if (!enabled) {
    // Compartir pantalla local
    setTimeout(() => {
      const screenTrack = activeRoom.localParticipant.getTrackPublication(
        Track.Source.ScreenShare
      );
      if (screenTrack && screenTrack.track) {
        const element = screenTrack.track.attach();
        element.style.width = "100%";
        element.style.height = "100%";
        element.style.objectFit = "contain";

        const container = document.getElementById("screenshare-container");
        if (container) {
          container.innerHTML = "";
          container.appendChild(element);
        }
      }
    }, 100);
  } else {
    // Dejar de compartir local
    const container = document.getElementById("screenshare-container");
    if (container) {
      container.innerHTML = "";
    }
  }

  return !enabled;
}

async function sendChatMessage(message) {
  if (!activeRoom) return false;
  const payload = new TextEncoder().encode(JSON.stringify(message));
  await activeRoom.localParticipant.publishData(payload, { reliable: true });
  return true;
}

async function muteAllRemoteParticipants() {
  if (!activeRoom) return;
  for (const [, participant] of activeRoom.remoteParticipants) {
    for (const [, publication] of participant.audioTrackPublications) {
      publication.setSubscribed(false);
    }
  }
}

async function switchRoom(
  roomName,
  participantName,
  isModerator = false,
  participantIdentity = "",
  options = {}
) {
  // connectToRoom ya desconecta la sala activa y limpia el grid
  return connectToRoom(
    roomName,
    participantName,
    isModerator,
    participantIdentity,
    options
  );
}

function getCurrentRoomName() {
  return activeRoom?.name || null;
}

function getLocalIdentity() {
  return activeRoom?.localParticipant?.identity || null;
}

function canPublish() {
  const permissions = activeRoom?.localParticipant?.permissions;
  return Boolean(permissions?.canPublish);
}

// TrackSource numérico del protocolo LiveKit: 1=camera, 2=microphone, 3=screen_share
const TRACK_SOURCE_IDS = { camera: 1, microphone: 2, screen_share: 3 };

function canPublishSource(sourceName) {
  const permissions = activeRoom?.localParticipant?.permissions;
  if (!permissions?.canPublish) return false;
  const sources = permissions.canPublishSources || [];
  if (!sources.length) return true; // sin restricción de fuentes
  return sources.includes(TRACK_SOURCE_IDS[sourceName]);
}

function getParticipantCount() {
  if (!activeRoom) return 0;
  return activeRoom.remoteParticipants.size + 1;
}

function getRemoteParticipants() {
  if (!activeRoom) return [];
  return Array.from(activeRoom.remoteParticipants.values());
}

function getConnectionState() {
  if (!activeRoom) return "disconnected";
  return activeRoom.state;
}

function isMicrophoneEnabled() {
  return Boolean(activeRoom?.localParticipant?.isMicrophoneEnabled);
}

function isCameraEnabled() {
  return Boolean(activeRoom?.localParticipant?.isCameraEnabled);
}

function getLocalTile() {
  const identity = activeRoom?.localParticipant?.identity;
  if (!identity) return null;
  return document.getElementById(`participant-${identity}`);
}

function attachLocalVideo() {
  if (!activeRoom) return;
  syncTiles();
  const videoTrack = activeRoom.localParticipant.getTrackPublication(
    Track.Source.Camera
  );

  if (videoTrack && videoTrack.track) {
    const element = videoTrack.track.attach();
    element.id = "video-local";
    element.style.position = "absolute";
    element.style.top = "0";
    element.style.left = "0";
    element.style.width = "100%";
    element.style.height = "100%";
    element.style.objectFit = "cover";
    element.style.transform = "scaleX(-1)";
    element.style.zIndex = "2";

    // El video propio va en su tile de la cuadrícula, como en Zoom
    const wrapper = getLocalTile();
    if (wrapper) {
      const existing = wrapper.querySelector("video");
      if (existing) {
        wrapper.removeChild(existing);
      }
      wrapper.appendChild(element);
    }
  }
}

function detachLocalVideo() {
  const wrapper = getLocalTile();
  if (wrapper) {
    const existing = wrapper.querySelector("video");
    if (existing) {
      wrapper.removeChild(existing);
    }
  }
}

export default {
  connectToRoom,
  switchRoom,
  getCurrentRoomName,
  getLocalIdentity,
  canPublish,
  canPublishSource,
  sendChatMessage,
  syncTiles,
  attachExistingTracks,
  disconnectFromRoom,
  toggleAudio,
  toggleVideo,
  toggleScreenShare,
  muteAllRemoteParticipants,
  getParticipantCount,
  getRemoteParticipants,
  getConnectionState,
  isMicrophoneEnabled,
  isCameraEnabled,
  getRoom,
  attachLocalVideo,
  detachLocalVideo,
};
